import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import PageLayout from "./PageLayout";
import { listContent, mediaUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

const fmt = (n) => `$${Number(n).toFixed(2)}`;
const CATEGORIES = ["All", "Hoodies", "Long Sleeve", "T-Shirts"];

export default function ShopPage() {
  const { addItem, setIsOpen } = useCart();
  const [filter, setFilter] = useState("All");
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    listContent("products")
      .then((items) => setAllProducts(items.filter((p) => p.published)))
      .catch(() => setAllProducts([]));
  }, []);

  const products = useMemo(
    () =>
      filter === "All"
        ? allProducts
        : allProducts.filter((p) => p.category === filter),
    [filter, allProducts]
  );

  const handleAdd = (product) => {
    addItem(product, 1);
    toast.success("Added to cart");
    setIsOpen(true);
  };

  return (
    <PageLayout
      title="Shop"
      subtitle="Wear your faith. Our “Beloved In Christ” collection is a gentle reminder of who you are in Him — crafted in comfortable, everyday styles."
    >
      <div className="max-w-6xl mx-auto px-5 py-14 md:py-20">
        {/* Filters */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`font-serif-body text-[14px] px-5 py-2 rounded-full border transition-colors ${
                filter === c
                  ? "bg-[#0f9fbc] border-[#0f9fbc] text-white"
                  : "bg-white border-[#d7e6ea] text-[#768487] hover:border-[#0f9fbc] hover:text-[#0f9fbc]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {products.map((p) => (
            <div
              key={p.id}
              className="group rounded-[10px] overflow-hidden border border-[#eaeaea] bg-white flex flex-col transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.08)]"
            >
              <div className="aspect-square overflow-hidden bg-[#f7f9fa]">
                <Link to={`/shop/${p.id}`}>
                  <img
                    src={mediaUrl(p.image)}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
              </div>
              <div className="p-4 flex flex-col flex-1">
                <span className="font-serif-body text-[11px] uppercase tracking-wide text-[#68c2d5] font-bold">
                  {p.category}
                </span>
                <Link to={`/shop/${p.id}`}>
                  <h3 className="font-serif-body text-[#4a4a4a] text-[14px] mt-1 leading-snug flex-1 hover:text-[#0f9fbc] transition-colors">
                    {p.name}
                  </h3>
                </Link>
                <p className="font-serif-display text-[#0f9fbc] text-lg font-bold mt-3">
                  {fmt(p.price)}
                </p>
                <button
                  onClick={() => handleAdd(p)}
                  className="mt-3 flex items-center justify-center gap-2 bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body text-[14px] font-bold py-2.5 rounded-[4px] transition-colors"
                >
                  <ShoppingCart size={16} /> Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageLayout>
  );
}
